<template>
  <div class="process-info-sidebar card-sidebar">
    <!-- Estado do processo -->
    <div
      v-if="process.state && process.state !== 'ongoing'"
      class="process-info-panel process-info-panel--state mb-3"
    >
      <span class="process-info-state-badge">
        {{ translateProcessStateToPt(process.state) }}
      </span>
    </div>

    <!-- Fases: timeline vertical -->
    <div
      v-if="process.phases && process.phases.length"
      class="process-info-panel process-info-panel--phases mb-3"
    >
      <h2 class="process-info-panel__title">
        <i
          class="pi pi-list process-info-panel__title-icon"
          aria-hidden="true"
        />
        {{ $t("process_phase") }}
      </h2>
      <ol class="process-info-stepper" aria-label="Fases do processo">
        <li
          v-for="(phase, index) in process.phases"
          :key="phase.id"
          class="process-info-stepper__item"
          :class="stepperItemClass(index)"
        >
          <div class="process-info-stepper__track" aria-hidden="true">
            <span class="process-info-stepper__dot">
              <i
                v-if="index < currentPhaseIndex"
                class="pi pi-check process-info-stepper__check"
                aria-hidden="true"
              />
              <span v-else class="process-info-stepper__num">{{
                index + 1
              }}</span>
            </span>
          </div>
          <div class="process-info-stepper__content">
            <span class="process-info-stepper__label">{{ phase.name }}</span>
          </div>
        </li>
      </ol>
    </div>

    <!-- Ficheiros -->
    <div
      v-if="process.files && process.files.length"
      class="process-info-panel process-info-panel--files mb-3"
    >
      <h2 class="process-info-panel__title">
        <i
          class="pi pi-folder-open process-info-panel__title-icon"
          aria-hidden="true"
        />
        {{ $t("files") }}
      </h2>

      <div
        v-for="(files, description) in process.filesOptimized"
        :key="description"
        class="process-info-files-group"
      >
        <div class="process-info-files-group__label">{{ description }}</div>
        <div v-for="file in files" :key="file.key">
          {{ file.userName }}
          <Button
            class="p-button-text w-12 mb-1 process-info-file-btn text-left"
            @click="setDisplayFiles(file.key, null)"
          >
            <img
              loading="lazy"
              :src="`${$root.images.filetypes}/${file.url
                .split('.')
                .pop()}.png`"
              width="30"
              height="30"
              alt="Tipo de ficheiro"
              class="mr-3"
              @error="$root.setDefaultFiletype"
            />
            {{ file.name }}
          </Button>
        </div>
      </div>

      <div
        v-for="(files, name) in process.opportunityFiles"
        :key="name"
        class="process-info-files-group"
      >
        <div class="process-info-files-group__label">{{ name }}</div>
        <div v-for="(file, key) in files" :key="key">
          {{ file.userName }}
          <Button
            class="p-button-text text-left w-12 process-info-file-btn"
            aria-haspopup="true"
            @click="setDisplayFiles(null, file.url)"
          >
            <img
              loading="lazy"
              :src="`${$root.images.filetypes}/${file.url
                .split('.')
                .pop()}.png`"
              width="30"
              height="30"
              alt="Tipo de ficheiro"
              @error="$root.setDefaultFiletype"
            />
            {{ file.name }}
          </Button>
        </div>
      </div>
    </div>

    <div class="process-info-panel process-info-panel--documentation mb-3">
      <DocumentationOverview
        :main-people="processDocumentationMainPeople"
        :family-people="processDocumentationFamilyPeople"
        :process-ids="processDocumentationProcessIds"
        :relation-process-ids="processRelationDocumentationProcessIds"
        family-is-relation
        :family-show-in-client-relations="false"
      />
    </div>

    <!-- Gestores -->
    <div
      v-if="projectManagersList.length"
      class="process-info-panel process-info-panel--contacts mb-3"
    >
      <h3 class="process-info-panel__title">
        <i
          class="pi pi-users process-info-panel__title-icon"
          aria-hidden="true"
        />
        {{ $t("process_project_managers") }}
      </h3>
      <div class="flex flex-column gap-3">
        <div
          v-for="person in projectManagersList"
          :key="'pm-' + (person.id || person.name)"
          class="process-info-contact-card"
        >
          <ShowUserImage
            :url="person.imgUrl"
            class="process-info-contact-card__photo flex-shrink-0"
            :img-alt="`Foto de ${person.name}`"
            width="64"
            height="64"
          />
          <div class="process-info-contact-card__body min-w-0 flex-1">
            <div class="process-info-contact-card__name">
              {{ person.name }}
            </div>
            <a
              v-if="person.email"
              :href="'mailto:' + person.email"
              class="process-info-contact-card__email"
            >
              <i
                class="pi pi-envelope process-info-contact-card__email-icon"
                aria-hidden="true"
              />
              {{ person.email }}
            </a>
            <span v-else class="process-info-contact-card__muted">—</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Consultor -->
    <div
      v-if="consultant"
      class="
        process-info-panel
        process-info-panel--contacts
        process-info-panel--consultant
        mb-2
      "
    >
      <h3 class="process-info-panel__title">
        <i
          class="pi pi-briefcase process-info-panel__title-icon"
          aria-hidden="true"
        />
        {{ $t("process_your_consultant") }}
      </h3>
      <div
        class="process-info-contact-card process-info-contact-card--highlight"
      >
        <ShowUserImage
          :url="consultant.imgUrl"
          class="process-info-contact-card__photo flex-shrink-0"
          :img-alt="`Foto de ${consultant.name}`"
          width="64"
          height="64"
        />
        <div class="process-info-contact-card__body min-w-0 flex-1">
          <div class="process-info-contact-card__name">
            {{ consultant.name }}
          </div>
          <a
            v-if="consultant.email"
            :href="'mailto:' + consultant.email"
            class="process-info-contact-card__email"
          >
            <i
              class="pi pi-envelope process-info-contact-card__email-icon"
              aria-hidden="true"
            />
            {{ consultant.email }}
          </a>
          <span v-else class="process-info-contact-card__muted">—</span>
        </div>
      </div>
    </div>
    <DialogFiles
      :files="process.files"
      :display-files="fileSelectedIndex != null"
      :file-selected-index="fileSelectedIndex"
      @closed="setDisplayFiles(null, null)"
    />
  </div>
</template>

<script>
import useDropdownOptions from "../../composables/useDropdownOptions.js";
import DialogFiles from "../files/DialogFiles.vue";
import ShowUserImage from "../files/ShowUserImage.vue";
import DocumentationOverview from "../clientDocumentations/DocumentationOverview.vue";
import { translateRelationToPt } from "../../utils/translateRelation";

export default {
  name: "ProcessInfo",
  components: { DialogFiles, DocumentationOverview, ShowUserImage },
  props: { process: { type: Object, default: null } },
  setup() {
    const { translateProcessStateToPt } = useDropdownOptions();
    return { translateProcessStateToPt };
  },
  data() {
    return { fileSelectedIndex: null, contacts: null };
  },
  computed: {
    processDocumentationProcessIds() {
      return this.process && this.process.id ? [this.process.id] : [];
    },
    processRelationDocumentationProcessIds() {
      return this.process && this.process.id ? [this.process.id] : [];
    },
    processClientIds() {
      const ids = [];
      if (this.process && this.process.client && this.process.client.id) {
        ids.push(this.process.client.id);
      }
      if (this.process && this.process.client2 && this.process.client2.id) {
        ids.push(this.process.client2.id);
      }
      return ids;
    },
    processDocumentationMainPeople() {
      const people = [];
      if (this.process && this.process.client && this.process.client.id) {
        people.push({
          id: this.process.client.id,
          name: this.process.client.name,
          imgUrl: this.process.client.imgUrl,
        });
      }
      if (this.process && this.process.client2 && this.process.client2.id) {
        people.push({
          id: this.process.client2.id,
          name: this.process.client2.name,
          imgUrl: this.process.client2.imgUrl,
        });
      }
      return people;
    },
    processDocumentationFamilyPeople() {
      const excluded = new Set(this.processClientIds);
      const seen = new Set();
      const people = [];
      const pushFrom = (relations) => {
        (relations || []).forEach((relation) => {
          const related = relation.client;
          if (!related || !related.id || !related.name) return;
          if (excluded.has(related.id) || seen.has(related.id)) return;
          seen.add(related.id);
          people.push({
            id: related.id,
            name: related.name,
            imgUrl: related.imgUrl,
            relationLabel: translateRelationToPt(relation.relation),
          });
        });
      };
      pushFrom(
        this.process &&
          this.process.client &&
          this.process.client.clientRelations
      );
      pushFrom(
        this.process &&
          this.process.client2 &&
          this.process.client2.clientRelations
      );
      return people;
    },
    projectManagersList() {
      const p = this.process;
      if (!p) {
        return [];
      }
      if (Array.isArray(p.projectManagers) && p.projectManagers.length) {
        return p.projectManagers;
      }
      if (!Array.isArray(p.users)) {
        return [];
      }
      const usersWithRoleName = p.users.filter(
        (user) => user && user.roleName === "manager"
      );
      if (usersWithRoleName.length) {
        return usersWithRoleName;
      }
      return p.users.filter((user) => {
        if (!user || !user.functionDescription) {
          return false;
        }
        return /gestor|manager/i.test(user.functionDescription);
      });
    },
    consultant() {
      const p = this.process;
      if (!p) {
        return this.contactAgent;
      }
      if (p.consultant && (p.consultant.id || p.consultant.name)) {
        return p.consultant;
      }
      if (p.agent && (p.agent.id || p.agent.name)) {
        return p.agent;
      }
      if (!Array.isArray(p.users)) {
        return null;
      }
      return (
        p.users.find((user) => user && user.roleName === "agent") ||
        p.users.find((user) => user && user.roleName === "agentAssistant") ||
        this.contactAgent
      );
    },
    contactAgent() {
      if (!this.contacts || !Array.isArray(this.contacts.agent)) {
        return null;
      }
      return this.contacts.agent[0] || null;
    },
    currentPhaseIndex() {
      const phases = this.process && this.process.phases;
      if (!phases || !phases.length) {
        return -1;
      }
      const pid = this.process && this.process.phase && this.process.phase.id;
      if (pid == null) {
        return 0;
      }
      const idx = phases.findIndex((p) => p.id == pid);
      return idx >= 0 ? idx : 0;
    },
  },
  created() {
    this.getContacts();
  },
  methods: {
    getContacts() {
      this.$root
        .apiGet(this.$root.api.profile, { params: { dataType: "contacts" } })
        .then((response) => {
          this.contacts = response.data.data;
        })
        .catch((error) => {
          if (error && error.response && error.response.data) {
            this.$root.toastError(error.response.data.msg_code);
          }
        });
    },
    stepperItemClass(index) {
      if (index < this.currentPhaseIndex) {
        return "is-done";
      }
      if (index === this.currentPhaseIndex) {
        return "is-current";
      }
      return "is-pending";
    },
    setDisplayFiles(index, url) {
      if (index == null) {
        index = this.process.files.findIndex(
          (file) => file && file.url === url
        );
        if (index == -1) {
          index = null;
        }
      }
      this.fileSelectedIndex = index;
    },
  },
};
</script>

<style scoped>
.process-info-sidebar {
  --pi-accent: #0f97c7;
  --pi-accent-soft: rgba(15, 151, 199, 0.12);
  --pi-accent-glow: rgba(15, 151, 199, 0.35);
  --pi-ink: #0c233f;
  --pi-ink-muted: #5c6578;
  --pi-surface: #ffffff;
  --pi-surface-elevated: #f6f8fb;
  --pi-border: #e4e9f0;
  --pi-purple-tint: rgba(64, 31, 78, 0.06);
  --pi-radius: 14px;
  --pi-radius-sm: 10px;
  --pi-shadow: 0 4px 14px rgba(12, 35, 63, 0.06);
  --pi-shadow-hover: 0 8px 24px rgba(12, 35, 63, 0.09);
}

/* Painéis */
.process-info-panel {
  background: var(--pi-surface);
  border: 1px solid var(--pi-border);
  border-radius: var(--pi-radius);
  padding: 1rem 1.1rem 1.15rem;
  box-shadow: var(--pi-shadow);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.process-info-panel:hover {
  box-shadow: var(--pi-shadow-hover);
}

.process-info-panel--state {
  background: linear-gradient(
    135deg,
    var(--pi-purple-tint) 0%,
    var(--pi-surface) 50%
  );
  border-color: rgba(64, 31, 78, 0.12);
  text-align: center;
  padding: 0.85rem 1rem;
}

.process-info-state-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #401f4e;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  background: rgba(64, 31, 78, 0.08);
  border: 1px solid rgba(64, 31, 78, 0.15);
}

.process-info-panel__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pi-ink);
  margin: 0 0 1rem;
  padding-bottom: 0.65rem;
  border-bottom: none;
  border-image: none;
  position: relative;
}

.process-info-panel__title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  background: linear-gradient(
    90deg,
    var(--pi-accent) 0%,
    rgba(15, 151, 199, 0.25) 55%,
    transparent 100%
  );
}

.process-info-panel__title-icon {
  font-size: 1rem;
  color: var(--pi-accent);
}

/* Timeline de fases */
.process-info-stepper {
  list-style: none;
  margin: 0;
  padding: 0;
}

.process-info-stepper__item {
  position: relative;
  display: grid;
  grid-template-columns: 2.25rem 1fr;
  gap: 0.65rem 0.85rem;
  padding-bottom: 1rem;
}

.process-info-stepper__item:last-child {
  padding-bottom: 0;
}

.process-info-stepper__item:not(:last-child)
  .process-info-stepper__track::after {
  content: "";
  position: absolute;
  top: 2.125rem;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: calc(100% - 0.5rem);
  border-radius: 2px;
  background: var(--pi-border);
}

.process-info-stepper__item.is-done:not(:last-child)
  .process-info-stepper__track::after {
  background: linear-gradient(
    180deg,
    var(--pi-accent) 0%,
    var(--pi-border) 100%
  );
}

.process-info-stepper__track {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 0.15rem;
}

.process-info-stepper__dot {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--pi-ink-muted);
  background: var(--pi-surface-elevated);
  border: 2px solid var(--pi-border);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.process-info-stepper__num {
  line-height: 1;
}

.process-info-stepper__check {
  font-size: 0.65rem;
  color: #fff;
}

.process-info-stepper__item.is-done .process-info-stepper__dot {
  background: var(--pi-accent);
  border-color: var(--pi-accent);
  color: #fff;
}

.process-info-stepper__item.is-current .process-info-stepper__dot {
  background: linear-gradient(145deg, #12a8db 0%, #0a7aa3 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 0 0 4px var(--pi-accent-soft), 0 4px 12px var(--pi-accent-glow);
  transform: scale(1.08);
}

.process-info-stepper__item.is-current .process-info-stepper__num {
  font-size: 0.75rem;
}

.process-info-stepper__content {
  padding-top: 0.05rem;
}

.process-info-stepper__label {
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--pi-ink-muted);
  word-break: break-word;
  display: block;
}

.process-info-stepper__item.is-current .process-info-stepper__label {
  color: var(--pi-ink);
  font-weight: 700;
}

.process-info-stepper__item.is-done .process-info-stepper__label {
  color: var(--pi-ink-muted);
}

/* Ficheiros dentro do painel */
.process-info-files-group {
  margin-bottom: 0.75rem;
}

.process-info-files-group:last-child {
  margin-bottom: 0;
}

.process-info-files-group__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--pi-ink-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.35rem;
  margin-top: 0.5rem;
}

.process-info-files-group:first-child .process-info-files-group__label {
  margin-top: 0;
}

.process-info-file-btn {
  border-radius: var(--pi-radius-sm) !important;
}

/* Nomes de ficheiro: preto; hover sublinhado (sem azul forte) */
.process-info-panel--files
  :deep(.process-info-file-btn.p-button.p-button-text) {
  color: var(--pi-ink) !important;
  justify-content: flex-start;
}

.process-info-panel--files
  :deep(.process-info-file-btn.p-button.p-button-text .p-button-label) {
  color: inherit !important;
}

.process-info-panel--files
  :deep(.process-info-file-btn.p-button.p-button-text:hover) {
  color: var(--pi-ink) !important;
  background: transparent !important;
}

.process-info-panel--files
  :deep(.process-info-file-btn.p-button.p-button-text:hover .p-button-label) {
  text-decoration: underline;
  color: inherit !important;
}

.process-info-panel--files
  :deep(.process-info-file-btn.p-button.p-button-text:focus) {
  box-shadow: none !important;
  color: var(--pi-ink) !important;
}

/* Cartões de contacto */
.process-info-contact-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1rem;
  background: var(--pi-surface-elevated);
  border-radius: var(--pi-radius-sm);
  border: 1px solid var(--pi-border);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.process-info-contact-card:hover {
  border-color: rgba(15, 151, 199, 0.35);
  background: #fff;
}

.process-info-contact-card--highlight {
  background: linear-gradient(
    135deg,
    rgba(15, 151, 199, 0.08) 0%,
    var(--pi-surface-elevated) 45%,
    #fff 100%
  );
  border-color: rgba(15, 151, 199, 0.22);
}

.process-info-contact-card__photo :deep(.img-user) {
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  border: 3px solid #fff;
  box-shadow: 0 2px 10px rgba(12, 35, 63, 0.12);
}

.process-info-contact-card__photo :deep(.p-skeleton) {
  border-radius: 50% !important;
}

.process-info-contact-card__name {
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--pi-ink);
  margin-bottom: 0.35rem;
}

.process-info-contact-card__email {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: var(--pi-ink);
  text-decoration: none;
  word-break: break-word;
  max-width: 100%;
  transition: color 0.15s ease, opacity 0.15s ease;
}

.process-info-contact-card__email:hover {
  color: var(--pi-ink);
  text-decoration: underline;
  opacity: 0.85;
}

.process-info-contact-card__email-icon {
  flex-shrink: 0;
  font-size: 0.8125rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}

.process-info-contact-card__muted {
  font-size: 0.8125rem;
  color: var(--pi-ink-muted);
}

@media screen and (min-width: 768px) {
  .process-info-panel {
    padding: 1.15rem 1.25rem 1.25rem;
  }

  .process-info-panel__title {
    font-size: 0.875rem;
  }

  .process-info-stepper__label {
    font-size: 0.875rem;
  }

  .process-info-contact-card__name {
    font-size: 1rem;
  }

  .process-info-contact-card__email {
    font-size: 0.875rem;
  }
}
</style>
